import { ArrowUpIcon } from "@heroicons/react/24/outline";
import { Button } from "@/core/components/ui/button";
import { Stack } from "@/core/components/ui/stack";
import { cn } from "@/core/utils/utils";
import { EDITOR_LIMITS } from "@/payroll/editor/constants";
import {
  AttachButton,
  EditorAttachments,
} from "@/payroll/editor/editor-documents";
import type { EditorUpload } from "@/payroll/editor/editor-documents/use-editor-upload";
import type { PayrollEditorQueries } from "@/payroll/editor/query";
import { QueuedMessages } from "./queued-messages";
import type { Composer } from "./use-composer";
import type { Outgoing } from "./use-outgoing";

/**
 * One input for everything: attachments, the message, and send. The textarea
 * grows with the text up to about five lines and then scrolls, using a grid
 * whose size is set by a mirrored copy of the value — no measuring on every
 * keystroke.
 */
export const EditorComposer = ({
  composer,
  documents,
  remove,
  upload,
  editable,
  disabled,
  blockedReason,
  open,
  busy,
  onSubmit,
  queued,
  onQueueSendNow,
  onQueueEdit,
  onQueueRemove,
}: {
  composer: Composer;
  documents: PayrollEditorQueries["documents"];
  remove: PayrollEditorQueries["remove"];
  upload: EditorUpload;
  editable: boolean;
  disabled: boolean;
  blockedReason: string;
  open: PayrollEditorQueries["open"];
  /** A request is running, so sending queues the message instead. */
  busy: boolean;
  onSubmit: () => void;
  /** Written while a request was running, waiting their turn. */
  queued: Outgoing[];
  onQueueSendNow: (id: string) => void;
  onQueueEdit: (id: string, message: string) => void;
  onQueueRemove: (id: string) => void;
}) => {
  if (!editable)
    return (
      <Stack
        gap="none"
        align="stretch"
        direction="row"
        className="flex flex-wrap items-center justify-between gap-3 border-t border-stroke-tertiary px-5 py-3"
      >
        <p className="text-secondary-text">
          This proposal is closed. Start a new one to keep going.
        </p>
        {/* In place. Volca publishing a proposal should not move the reader to
            a different screen with a different address; the same screen simply
            has a new proposal on it. */}
        <Button
          type="button"
          size="xs"
          variant="tertiary"
          loading={open.isPending}
          onClick={() => open.mutate()}
        >
          Start a new proposal
        </Button>
      </Stack>
    );
  const canSend = !disabled && !!composer.text.trim();
  return (
    <form
      // No padding above the input. The transcript scrolls right into it and
      // fades at the seam; four pixels of ground between the two turned that
      // fade into a cut line with a grey band under it.
      className="flex flex-col gap-2 px-5 pb-6 pt-0"
      onSubmit={(event) => {
        event.preventDefault();
        if (canSend) onSubmit();
      }}
    >
      <QueuedMessages
        queued={queued}
        onSendNow={onQueueSendNow}
        onEdit={onQueueEdit}
        onRemove={onQueueRemove}
      />

      {/* The whole box is the field. Most of it is padding and controls, and a
          click that landed on any of that used to do nothing, so the box read
          as inert. `mousedown` rather than `click`, so the caret arrives before
          the press completes; `preventDefault` keeps the browser from moving
          focus somewhere else on the way. Clicks on the real controls are left
          alone — the attach button and send must keep their own behaviour. */}
      <div
        // 20px radius and a hairline border, both off the scale. At `rounded-2xl`
        // with a full-weight border the box read as a form field; the reference
        // composers read as a surface you write on, which is a softer corner and
        // a lighter edge carrying a real shadow.
        className="relative rounded-[20px] border border-stroke-tertiary bg-surface-primary shadow-[0_1px_2px_rgba(16,24,40,0.06),0_8px_24px_-12px_rgba(16,24,40,0.18)] transition-colors focus-within:border-stroke-secondary"
        onMouseDown={(event) => {
          if (
            event.target instanceof Element &&
            event.target.closest("button, a, input, textarea, [role='button']")
          )
            return;
          event.preventDefault();
          composer.field.current?.focus();
        }}
      >
        <label htmlFor="payroll-change" className="sr-only">
          Describe your payroll change
        </label>
        {/* One row: the words, then the two controls. Empty, the box is one
            line of text tall and nothing more. `items-end` is what keeps the
            controls on the last line as the text grows, rather than letting
            them ride up the middle of a tall field.

            An invisible mirror of the value sizes the text column, so the
            field grows with the text without measuring it on every
            keystroke. */}
        <Stack
          direction="row"
          gap="none"
          align="end"
          className="flex items-end gap-1 py-1.5 pl-3.5 pr-1.5"
        >
          <div className="grid max-h-40 min-w-0 flex-1 overflow-y-auto py-1 text-md leading-5">
            <span
              aria-hidden
              className="invisible col-start-1 row-start-1 whitespace-pre-wrap break-words"
            >
              {`${composer.text} `}
            </span>
            <textarea
              ref={composer.field}
              id="payroll-change"
              rows={1}
              maxLength={EDITOR_LIMITS.messageLength}
              value={composer.text}
              onChange={(event) => composer.setText(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  if (canSend) onSubmit();
                }
              }}
              // Short enough to survive the narrowest pane. A placeholder is
              // one line that cannot wrap, and the long form of this one was
              // cut off mid-sentence. What it explained is said under the
              // field instead, where it has room to wrap.
              placeholder={
                busy ? "Add another change…" : "Describe the payroll change…"
              }
              className="col-start-1 row-start-1 m-0 resize-none whitespace-pre-wrap break-words border-0 bg-transparent p-0 leading-5 outline-none placeholder:text-tertiary-text disabled:opacity-60"
            />
          </div>

          <AttachButton
            documents={documents}
            upload={upload}
            disabled={disabled}
          />
          <Button
            type="submit"
            size="icon"
            // The one piece of weight in the row, and round like the box it
            // sits in. 28px is the height of one line of text with its
            // padding, so an empty box is a single row with the words, the
            // plus and this all on it.
            //
            // It keeps a solid shape with nothing to send, rather than fading
            // towards the background: the reference composers always show the
            // control, and a washed-out disc reads as something broken rather
            // than as something waiting.
            className={cn("h-7 w-7 min-w-7 shrink-0 rounded-full", {
              "bg-gray-15 text-tertiary-text opacity-100 hover:bg-gray-15":
                !canSend,
            })}
            disabled={!canSend}
            aria-label={busy ? "Queue request" : "Send request"}
            title={
              blockedReason ||
              (busy
                ? "Queue this. It sends when the current request finishes."
                : "Send request")
            }
          >
            <ArrowUpIcon className="h-3.5 w-3.5" />
          </Button>
        </Stack>
      </div>

      {/* The files land under the field, below the control that added them. */}
      <EditorAttachments
        documents={documents}
        remove={remove}
        upload={upload}
        disabled={disabled}
        canRemove={editable}
      />
      {/* Under the field, and only while something is running. It used to sit
          in the transcript under every progress line, where it was repeated
          advice the reader had already taken. Here it reads as a note about
          the thing they are typing into: how long the wait is, and that they
          do not have to wait to write the next change. */}
      {busy && (
        <p className="px-2 text-sm text-tertiary-text">
          This usually takes a few minutes. You can leave and come back, or
          write the next change now and it sends when this one finishes.
        </p>
      )}
      {!!blockedReason && (
        <p className="px-2 text-sm text-secondary-text">{blockedReason}</p>
      )}
    </form>
  );
};
